import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import { listSearchSchema } from '@/shared/patterns/list-search';

import { creditNoteApprovalFilters, internetBankingFilters } from './approvals.filters';

// Caminhos e buscas da Central de Aprovações. O hub mora no caminho do menu (`/aprovacoes`, como a
// Conciliação em `/concilicao`, S28/S29) e as abas do legado viram `?tab=`; os caminhos antigos redirecionam.

export const APPROVALS_PATH = '/aprovacoes';
/** Hub do legado (router.tsx:709-713): a primeira aba, Internet Banking. */
export const LEGACY_HUB_PATH = '/aprovacoes/gestao-aprovacoes';
/** Aba "Solicitações de conta" do legado (router.tsx:719-726). */
export const LEGACY_REQUESTS_PATH = '/aprovacoes/gestao-aprovacoes/solicitacoes';
/** "Operações", só por URL, sem aba no hub (router.tsx:727-731): tela própria no mesmo caminho, como Operadores (R6). */
export const CREDIT_NOTE_APPROVALS_PATH = '/aprovacoes/gestao-aprovacoes/operacoes';
/**
 * Destino das notificações de pendência (notificationTypeEnum.ts:144-180), que no legado leva a uma rota que não
 * existe. No novo ela abre a aba Internet Banking no tipo do `typeOfPendingApprovals` (A3).
 */
export const LEGACY_INTERNET_BANKING_PATH = '/aprovacoes/gestao-aprovacoes/internet-banking';
/** "Operadores", só por URL (router.tsx:732-742), com caminho próprio no novo também. */
export const OPERATORS_PATH = '/aprovacoes/gestao-aprovacoes/operadores';

/** Abas na ordem do legado (ApprovalsFormContainer.tsx:5-8). */
export const APPROVALS_TAB_IDS = ['internet-banking', 'solicitacoes'] as const;
export type ApprovalsTab = (typeof APPROVALS_TAB_IDS)[number];
export const APPROVALS_TABS = [
  { id: 'internet-banking', label: msg`Internet Banking`, icon: 'landmark' },
  { id: 'solicitacoes', label: msg`Solicitações de conta`, icon: 'file-text' },
] as const;

/**
 * Os seis tipos da aba Internet Banking, na ordem do seletor (SelectBankAccountApprovalType.tsx:12-19). O valor é o
 * `approvalResource` do fio; o padrão é `BankAccountUser` (BankAccountListContainer.tsx:60).
 */
/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio */
export const IB_TYPES = ['BankAccountUser', 'BankAccountTransfer', 'BankAccountPayment', 'BankAccountBeneficiary', 'BankAccountTransactionLimits', 'ComplianceReview'] as const;
/* eslint-enable lingui/no-unlocalized-strings */
export type IbType = (typeof IB_TYPES)[number];
export const DEFAULT_IB_TYPE: IbType = 'BankAccountUser';

/** Rótulo do botão do seletor (SelectBankAccountApprovalType.tsx:30-37). */
export const IB_TYPE_LABELS: Readonly<Record<IbType, ReturnType<typeof msg>>> = {
  BankAccountUser: msg`Operadores`,
  BankAccountTransfer: msg`Transferência`,
  BankAccountPayment: msg`Pagamentos`,
  BankAccountBeneficiary: msg`Favorecidos`,
  BankAccountTransactionLimits: msg`Limites`,
  ComplianceReview: msg`Revisão de Compliance`,
};

/** Título da página com o tipo escolhido (ApprovalsHeader.tsx:16-25): "Transferências", no plural, ao contrário do botão. */
export const IB_TYPE_TITLES: Readonly<Record<IbType, ReturnType<typeof msg>>> = {
  BankAccountUser: msg`Aprovações de Operadores`,
  BankAccountTransfer: msg`Aprovações de Transferências`,
  BankAccountPayment: msg`Aprovações de Pagamentos`,
  BankAccountBeneficiary: msg`Aprovações de Favorecidos`,
  BankAccountTransactionLimits: msg`Aprovações de Limites`,
  ComplianceReview: msg`Aprovações de Revisão de Compliance`,
};

/**
 * `?tab=` (a primeira não vai para a URL) e a página; na aba Internet Banking, também `?tipo=` (o padrão não vai
 * para a URL) e os filtros, que valem para todos os tipos, como no legado (um `FilterProvider` só).
 */
export const approvalsSearch = listSearchSchema({
  tab: z.enum(APPROVALS_TAB_IDS).optional().catch(undefined),
  tipo: z.enum(IB_TYPES).optional().catch(undefined),
  ...internetBankingFilters.shape,
});

/** A rota das notificações (A3): só o tipo; qualquer outro valor cai no padrão. */
export const legacyInternetBankingSearch = z.object({ typeOfPendingApprovals: z.enum(IB_TYPES).optional().catch(undefined) });
export type ApprovalsSearch = z.output<typeof approvalsSearch>;

export const operatorsSearch = listSearchSchema({});

/** Operações: a página e os filtros (CreditNoteApprovListWithFilters.tsx:43-75), que moram na URL como no legado. */
export const creditNoteApprovalsSearch = listSearchSchema({ ...creditNoteApprovalFilters.shape });
export type CreditNoteApprovalsSearch = z.output<typeof creditNoteApprovalsSearch>;
